import React, { useCallback, useRef, useEffect, useState } from 'react';
import { debounce } from 'underscore';
import PropTypes from 'prop-types';
import {
  Button,
  Caption1,
  mergeClasses,
} from '@fluentui/react-components';
import {
  RealUsedFlagSuffix,
  handleRealUsedFlag,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import imageUploadIcon from './image-upload-icon.svg';
import background1 from './background1.jpg';
import background2 from './background2.jpg';
import background3 from './background3.jpg';
import background4 from './background4.jpg';
import background5 from './background5.jpg';
import background6 from './background6.jpg';
import background7 from './background7.jpg';
import background8 from './background8.jpg';
import background9 from './background9.jpg';
import background10 from './background10.jpg';
import { useImageBackgroundSectionStyles } from '../../home-page-styles/component-styles/home-page-image-background-section';

const staticBackgrounds = [
  background1,
  background2,
  background3,
  background4,
  background5,
  background6,
  background7,
  background8,
  background9,
  background10,
];

export const ImageBackgroundCards = ({
  i18n, navigation, loadingStatus, isAIGCRegionRestricted, notificationApi, adsStudioAccountPreferences,
}) => {
  const styles = useImageBackgroundSectionStyles();
  const [exampleList, setExampleList] = useState([]);

  const handleImageUploadClick = useCallback(() => {
    if (isAIGCRegionRestricted) {
      notificationApi.showWarning(i18n.getString(_TL_('This feature isn\'t available in all markets.')));
    } else {
      navigation.gotoBackgroundRemovalModal();
    }
    handleRealUsedFlag(adsStudioAccountPreferences, RealUsedFlagSuffix.App);
  }, [isAIGCRegionRestricted, adsStudioAccountPreferences, notificationApi, i18n, navigation]);

  const onExampleImageKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleImageUploadClick();
    }
  }, [handleImageUploadClick]);

  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!loadingStatus) {
      const wrapperWidth = wrapperRef.current ? wrapperRef.current.offsetWidth : 0;
      const maxCount = wrapperWidth > 0 ? Math.floor(wrapperWidth / 264) : 1;
      const newList = staticBackgrounds.slice(0, maxCount - 1);
      setExampleList(newList);
    } else {
      setExampleList([]);
    }
  }, [loadingStatus]);

  useEffect(() => {
    const updateExampleList = debounce((entries) => {
      if (entries[0]) {
        const { clientWidth } = entries[0].target;

        if (!loadingStatus) {
          const maxCount = clientWidth > 0 ? Math.floor(clientWidth / 264) : 1;
          const newList = staticBackgrounds.slice(0, maxCount - 1);
          setExampleList(newList);
        }
      }
    }, 250);
    const resizeObserver = window.ResizeObserver && new ResizeObserver(updateExampleList);
    if (wrapperRef.current && resizeObserver) {
      resizeObserver.observe(wrapperRef.current);
    }
    return () => {
      if (wrapperRef.current && resizeObserver) {
        resizeObserver.unobserve(wrapperRef.current);
      }
    };
  }, [loadingStatus]);

  return (
    <>
      <div className={styles.subHeading} ref={wrapperRef} role="heading" aria-level="2">
        {i18n.getString(_TL_('Try a different background'))}
      </div>
      <div className={styles.paragraph}>
        {i18n.getString(_TL_('Try out a new background for your image'))}
      </div>

      <div className={mergeClasses('background-card-container', styles.backgroundCardContainer)}>
        <Button className={mergeClasses('background-removal-button', styles.uploadCard)} onClick={() => handleImageUploadClick()}>
          <div className={styles.uploadIcon}>
            <img src={imageUploadIcon} alt="Upload" />
          </div>
          <div className={styles.uploadFrame}>
            <div className={styles.uploadCaption}>
              <Caption1>{i18n.getString(_TL_('Upload or choose an existing image to start'))}</Caption1>
            </div>
            <div className={styles.uploadButton} >
              {i18n.getString(_TL_('Choose an image'))}
            </div>
          </div>
        </Button>
        {exampleList.length > 0 && exampleList.map((background, index) => (
          <div
            key={background}
            className={mergeClasses('background-example-card', styles.imageCard)}
            tabIndex={0}
            role="button"
            onClick={handleImageUploadClick}
            onKeyDown={onExampleImageKeyDown}
          >
            <img src={background} className={styles.image} alt={`Background ${index + 1}`} />
          </div>
        ))}
      </div>
    </>
  );
};

ImageBackgroundCards.propTypes = {
  oDataConfig: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  navigation: PropTypes.shape({
    gotoUploadFilesPanelPage: PropTypes.func,
  }).isRequired,
  isAIGCRegionRestricted: PropTypes.bool.isRequired,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func.isRequired,
  }).isRequired,
};
